import React, { useState, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import { toast } from '../../common/Toast'
import CheckboxInput from '../../common/CheckboxInput'
import ExpandableSection from '../../common/ExpandableSection'
import SimpleTextInput from '../../common/SimpleTextInput'
import TooltipedIcon from '../../common/TooltipedIcon'
import { CalendarIcon, CloseCircleIcon, InformationIcon, UrlIcon } from '../../../icons'
import { capitalizeFirstLetter, capitalizeWords } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { videosUploadWritePermission } from '../../../utils/permissionValidation'
import { createVideo, updateVideo } from '../../../actions/videos'
import countries from '../../../utils/countries.json'
import { getAvailableGroup } from '../../../utils/userGroups'
import '../uploads.scss'

const AddEditVideoModal = (props) => {
  const { t } = useTranslation()
  const { selectedVideo, closeModal, showSpinner } = props
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [videoId, setVideoId] = useState('')
  const [url, setUrl] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [videoLastUpdateDate, setVideoLastUpdateDate] = useState(null)
  const [accessLevel, setAccessLevel] = useState(null)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [videoType, setVideoType] = useState(null)
  const [selectedRegions, setSelectedRegions] = useState([])
  const [language, setLanguage] = useState(null)
  const [selectedModels, setSelectedModels] = useState([])
  const [status, setStatus] = useState(false)
  const [expandedItem, setExpandedItem] = useState(null)
  const [modelsOptions, setModelsOptions] = useState([])
  const [haveError, setHaveError] = useState({})
  const videoStatuses = ['inactive', 'active']
  const DESCRIPTION_MAX = 300

  useLayoutEffect(() => {
    const sortedModels = props.videoModels.sort((a, b) => a?.localeCompare(b))
    setModelsOptions(sortedModels)
  }, [props.videoModels])

  useLayoutEffect(() => {
    if (selectedVideo) {
      setTitle(selectedVideo.title)
      setDescription(selectedVideo.description || '')
      setVideoId(selectedVideo.videoId)
      setUrl(selectedVideo.url)
      setThumbnailUrl(selectedVideo.thumbnailUrl)
      setVideoLastUpdateDate(selectedVideo.videoLastUpdateDate)
      setStatus(selectedVideo.isActive)
      setAccessLevel(selectedVideo.accessLevel)
      setSelectedCategories(selectedVideo.categories || [])
      setVideoType(selectedVideo.videoType)
      setSelectedRegions(JSON.parse(JSON.stringify(selectedVideo.regions || [])))
      setLanguage(selectedVideo.languageCode)
      setSelectedModels(selectedVideo.models || [])
    }
  }, [selectedVideo])

  const videoHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('video_context.status_toggle_text')}`} orientation="bottom" />
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={status} onChange={() => setStatus((prevValue) => !prevValue)} isDisabled={showSpinner} />
        <div className="status">{t(videoStatuses[Number(status)])}</div>
      </div>
    )
  }

  const videoFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleAddEditUploadVideo}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={videosUploadWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className={'custom-date-input'} ref={ref} onClick={onClick}>
      <div className={'date-text' + (!value ? ' placeholder-text' : '')}>{value ? value : `${t('please_select')}`}</div>
      {value && (
        <button
          className="reset-date-button"
          onClick={(e) => {
            e.stopPropagation()
            setVideoLastUpdateDate(null)
          }}
        >
          <CloseCircleIcon />
        </button>
      )}
      <CalendarIcon className="calendar-icon" />
    </div>
  ))

  const hasSameRegions = (currentRegions, initialRegions) => {
    const idsCurr = currentRegions.map((r) => r.id).sort()
    const idsInit = initialRegions.map((r) => r.id).sort()

    return idsCurr.length === idsInit.length && idsCurr.every((id, index) => id === idsInit[index])
  }

  const hasSameInput = () => {
    return (
      selectedVideo &&
      title === selectedVideo.title &&
      description === (selectedVideo.description || '') &&
      videoId === selectedVideo.videoId &&
      url === selectedVideo.url &&
      thumbnailUrl === selectedVideo.thumbnailUrl &&
      accessLevel === selectedVideo.accessLevel &&
      selectedCategories.length === selectedVideo.categories?.length &&
      selectedCategories.every((category) => selectedVideo.categories?.includes(category)) &&
      videoType === selectedVideo.videoType &&
      language === selectedVideo.languageCode &&
      status === selectedVideo.isActive &&
      videoLastUpdateDate === selectedVideo.videoLastUpdateDate &&
      hasSameRegions(selectedRegions, selectedVideo.regions) &&
      selectedModels.length === selectedVideo.models?.length &&
      selectedModels.every((model) => selectedVideo.models?.includes(model))
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!title.trim()) {
      errors = { ...errors, title: true }
      isValid = false
    }

    if (!videoId.trim()) {
      errors = { ...errors, videoId: true }
      isValid = false
    }

    if (!url.trim()) {
      errors = { ...errors, url: true }
      isValid = false
    }

    if (!thumbnailUrl.trim()) {
      errors = { ...errors, thumbnailUrl: true }
      isValid = false
    }

    if (!accessLevel) {
      errors = { ...errors, accessLevel: true }
      isValid = false
    }

    if (selectedCategories.length === 0) {
      errors = { ...errors, categories: true }
      isValid = false
    }

    if (!videoType) {
      errors = { ...errors, videoType: true }
      isValid = false
    }

    if (!language) {
      errors = { ...errors, language: true }
      isValid = false
    }

    if (selectedRegions.length === 0 || selectedRegions.every((region) => !region.countries || region.countries.length === 0)) {
      errors = { ...errors, regions: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleAddEditUploadVideo = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const bodyParams = {
      title: title.trim(),
      ...(description.trim().length && { description: description.trim() }),
      videoId: videoId.trim(),
      url: url.trim(),
      thumbnailUrl: thumbnailUrl.trim(),
      isActive: status,
      accessLevelId: props.accessLevels.find((access) => access.name === accessLevel)?.id,
      categories: selectedCategories.map((category) => props.videoCategories.find((c) => c.name === category)?.id),
      videoTypeId: props.videoTypes.find((t) => t.name === videoType)?.id,
      languageCode: language,
      regions: selectedRegions.map((region) => ({
        id: region.id,
        countries: props.regions.find((r) => r.id === region.id)?.countries?.length === region.countries.length ? ['ALL'] : region.countries,
      })),
      models: selectedModels,
      videoLastUpdateDate: videoLastUpdateDate,
    }

    if (selectedVideo) {
      props.actions.updateVideo(selectedVideo.id, bodyParams).then(() => {
        closeModal()
      })
      return
    }

    props.actions.createVideo(bodyParams).then(() => {
      closeModal()
    })
  }

  const toggleExpand = (index) => {
    setExpandedItem(expandedItem === index ? null : index)
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={selectedVideo ? t('video_context.edit_video') : t('video_context.add_new_video')}
        headerContent={videoHeaderContent()}
        footerContent={videoFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('title')}*`}</p>
              <SimpleTextInput value={title} hasError={haveError['title']} onChange={(value) => setTitle(value)} onClear={() => setTitle('')} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('description')}`}</p>
              <div className="textarea-with-counter">
                <textarea
                  className={`textarea-input ${haveError['description'] ? 'error' : ''}`}
                  value={description}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
                />
                {description && (
                  <button type="button" className="clear-button" onClick={() => setDescription('')} aria-label="Clear description">
                    ✕
                  </button>
                )}
                <div className="char-counter">{`${description.length}/${DESCRIPTION_MAX}`}</div>
              </div>
              <div className="small-disclaimer">{`${t('video_context.description_max_length')}`}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('url')}*`}</p>
              <SimpleTextInput icon={<UrlIcon />} value={url} hasError={haveError['url']} onChange={(value) => setUrl(value)} onClear={() => setUrl('')} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('video_id')}*`}</p>
              <SimpleTextInput value={videoId} hasError={haveError['videoId']} onChange={(value) => setVideoId(value)} onClear={() => setVideoId('')} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {`${t('video_context.thumbnail_url')}*`}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('video_context.thumbnail_url_text')}`} />
              </p>
              <SimpleTextInput
                icon={<UrlIcon />}
                value={thumbnailUrl}
                hasError={haveError['thumbnailUrl']}
                onChange={(value) => setThumbnailUrl(value)}
                onClear={() => setThumbnailUrl('')}
              />
              <div className="small-disclaimer">{'https://img.youtube.com/vi/{ID}/0.jpg'}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('settings').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('access_level')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.accessLevels.map((accessLevel) => ({ key: accessLevel.name, value: capitalizeFirstLetter(accessLevel.name) }))}
                initialSelectedKey={accessLevel}
                onChange={(accessLevel) => setAccessLevel(accessLevel.key)}
                hasError={haveError['accessLevel']}
              />
              {accessLevel && <div className="small-disclaimer">{`${t('available_to')}: ${getAvailableGroup(accessLevel)}`}</div>}
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${t('video_context.video_type')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.videoTypes.map((typeOption) => ({ key: typeOption.name, value: capitalizeFirstLetter(typeOption.name) }))}
                initialSelectedKey={videoType}
                onChange={(type) => setVideoType(type.key)}
                hasError={haveError['videoType']}
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('categories')}*`}</p>
              {haveError[`categories`] && <span className="error-message">{t('error.must_select_one_category')}</span>}
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {props.videoCategories.map((category, index) => (
                    <div className="item-wrapper" key={index}>
                      <CheckboxInput
                        checked={selectedCategories.includes(category.name)}
                        text={category.name}
                        onChange={() =>
                          setSelectedCategories((prevValue) =>
                            prevValue.includes(category.name) ? prevValue.filter((value) => value !== category.name) : [...prevValue, category.name],
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('regions')}*`}</p>
              {haveError[`regions`] && <span className="error-message">{t('error.must_select_one_region')}</span>}
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {props.regions.map((region, index) => (
                    <div className="item-wrapper" key={index}>
                      <CheckboxInput
                        checked={selectedRegions?.map((r) => r.name).includes(region.name)}
                        text={region.name}
                        onChange={() =>
                          setSelectedRegions((prevValue) =>
                            prevValue.map((r) => r.name).includes(region.name)
                              ? prevValue.filter((r) => r.name !== region.name)
                              : [...prevValue, JSON.parse(JSON.stringify(region))],
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {false &&
            selectedRegions
              ?.sort((a, b) => a.name?.localeCompare(b.name))
              ?.map((region, index) => (
                <div className="row-wrapper" key={`countries-${index}`}>
                  <ExpandableSection
                    title={`${region.name} ${t('countries')}`}
                    isExpanded={expandedItem === index}
                    onToggle={() => toggleExpand(index)}
                    className="internal-expandable"
                    additionalContent={
                      expandedItem === index ? (
                        <div className="expandable-action">
                          <span
                            className={props.regions.find((r) => r.name === region.name)?.countries?.length !== region.countries?.length ? 'available' : ''}
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRegions((prev) =>
                                prev.map((r) =>
                                  r.name === region.name ? { ...r, countries: props.regions.find((reg) => reg.name === region.name)?.countries || [] } : r,
                                ),
                              )
                            }}
                          >
                            {t('select_all')}
                          </span>
                          <span>|</span>
                          <span
                            className={region.countries?.length > 0 ? 'available' : ''}
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRegions((prev) => prev.map((r) => (r.name === region.name ? { ...r, countries: [] } : r)))
                            }}
                          >
                            {capitalizeWords(t('clear_all'))}
                          </span>
                        </div>
                      ) : (
                        <div className="counter-badge">
                          {region.countries?.includes('ALL')
                            ? props.regions.find((r) => r.name === region.name)?.countries?.length
                            : region.countries?.length || 0}
                        </div>
                      )
                    }
                  >
                    <div className="ordered-list-wrapper">
                      <div className="columns-wrapper">
                        {props.regions
                          .find((r) => r.name === region.name)
                          ?.countries?.map((country) => (
                            <div className="item-wrapper" key={country}>
                              <CheckboxInput
                                checked={region.countries?.includes(country) || region.countries?.includes('ALL')}
                                text={countries.find((c) => c.code === country)?.name || ''}
                                onChange={() =>
                                  setSelectedRegions((prevValue) =>
                                    prevValue.map((r) =>
                                      r.name === region.name
                                        ? {
                                            ...r,
                                            countries: r.countries.includes(country) ? r.countries.filter((c) => c !== country) : [...r.countries, country],
                                          }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  </ExpandableSection>
                </div>
              ))}
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('language')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.languages.map((languageOption) => ({
                  key: languageOption,
                  value: t(languagesShort[languageOption.toLowerCase()] || languageOption),
                }))}
                initialSelectedKey={language}
                onChange={(lang) => setLanguage(lang.key)}
                hasError={haveError['language']}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${capitalizeWords(t('last_updated'))}`}</p>
              <DatePicker
                selected={
                  videoLastUpdateDate
                    ? new Date(
                        new Date(videoLastUpdateDate).getUTCFullYear(),
                        new Date(videoLastUpdateDate).getUTCMonth(),
                        new Date(videoLastUpdateDate).getUTCDate(),
                      )
                    : ''
                }
                onChange={(date) => {
                  setVideoLastUpdateDate(new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString())
                }}
                dateFormat={'yyyy-MM-dd'}
                yearItemNumber={9}
                customInput={<CustomDateInput />}
                showYearDropdown
                showMonthDropdown
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('monitoring.models')}</p>
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {modelsOptions.map((model, index) => (
                    <div className="item-wrapper" key={index}>
                      <CheckboxInput
                        checked={selectedModels.includes(model)}
                        text={model}
                        onChange={() =>
                          setSelectedModels((prevValue) => (prevValue.includes(model) ? prevValue.filter((value) => value !== model) : [...prevValue, model]))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

function stateToProps({ videos, shared }) {
  return {
    accessLevels: shared?.accessLevels || [],
    languages: shared?.languages?.map((l) => l.languageCode) || [],
    regions: shared?.regions || [],
    videoCategories: videos?.adminVideoCategories || [],
    videoModels: [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])],
    videoTypes: videos?.adminVideoTypes || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createVideo,
        updateVideo,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditVideoModal)

